import React from 'react';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import { NodeLatest } from '../lib/iotApi';

export interface TelemetryPoint {
  ts?: string;
  measures?: NodeLatest['measures'];
}

interface Props {
  data: TelemetryPoint[];
}

const TelemetryChart: React.FC<Props> = ({ data }) => {
  const points = data.map((d) => {
    const t = d.ts ? new Date(d.ts) : new Date();
    return {
      time: t.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      flow: d.measures?.flow_velocity_lpm ?? 0,
      battery: d.measures?.battery_v ?? null,
      valve: (d.measures?.valve_state ?? 0) === 1 ? 1 : 0,
    };
  });

  if (points.length === 0) {
    return (
      <div className="text-center py-6 text-sm text-[var(--text-secondary)]" data-testid="telemetry-empty">
        Aucune donnée de télémétrie
      </div>
    );
  }

  return (
    <div data-testid="telemetry-chart">
      <ResponsiveContainer width="100%" height={220}>
        <ComposedChart data={points} margin={{ top: 8, right: 8, left: -12, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="var(--border-light)" />
          <XAxis dataKey="time" tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} interval="preserveStartEnd" minTickGap={40} />
          <YAxis yAxisId="left" tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} />
          <YAxis yAxisId="right" orientation="right" domain={[3, 4.2]} tick={{ fontSize: 10, fill: 'var(--text-tertiary)' }} />
          <Tooltip
            contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid var(--border-light)' }}
            formatter={(value, name) => {
              const label = String(name ?? '');
              const scalar = Array.isArray(value) ? value[0] : value;
              if (scalar == null) return ['—', label];
              if (label === 'Débit (L/min)') return [`${scalar} L/min`, label];
              if (label === 'Batterie (V)') return [`${scalar} V`, label];
              if (label === 'Vanne') return [Number(scalar) === 1 ? 'Ouverte' : 'Fermée', label];
              return [scalar, label];
            }}
          />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          {/* Valve state as a shaded background band (0/1 scaled to left axis max) */}
          <Area
            yAxisId="left"
            type="stepAfter"
            dataKey="valve"
            name="Vanne"
            stroke="none"
            fill="var(--color-success)"
            fillOpacity={0.12}
            isAnimationActive={false}
          />
          <Area
            yAxisId="left"
            type="monotone"
            dataKey="flow"
            name="Débit (L/min)"
            stroke="var(--color-primary)"
            fill="var(--color-primary)"
            fillOpacity={0.2}
            strokeWidth={2}
            isAnimationActive={false}
          />
          <Line
            yAxisId="right"
            type="monotone"
            dataKey="battery"
            name="Batterie (V)"
            stroke="var(--color-info)"
            strokeWidth={2}
            dot={false}
            connectNulls
            isAnimationActive={false}
          />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
};

export default TelemetryChart;
